import PrimaryButton from '@/components/Buttons/PrimaryButton';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Evaluation } from '@/types';
import { router } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface CertificateProps {
    evaluation: Evaluation;
    certificate_url: string | null;
    download_url?: string | null;
    status: 'ready' | 'generating';
    course?: {
        slug: string;
    };
}

const CertificateContent: React.FC<CertificateProps> = ({ evaluation, certificate_url, download_url, status, course }) => {
    const { t } = useTranslation();
    const pageTitle = `Certificado - ${evaluation.title}`;

    const handleReturnToCourse = () => {
        if (course?.slug) {
            router.get(`/courses/${course.slug}/`);
            return;
        }

        router.get(`/evaluations/${evaluation.id}/results/`);
    };

    const handleDownload = () => {
        const url = download_url || certificate_url;
        if (url) {
            window.open(url, '_blank');
        }
    };

    const handleRefresh = () => {
        window.location.reload();
    };

    return (
        <AppLayout>
            <PageTitle title={pageTitle} />
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
                <div className="max-w-4xl w-full bg-white rounded-lg shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="px-8 py-6 border-b border-gray-200">
                        <div className="text-center">
                            <h1 className="text-2xl font-bold text-gray-800 mb-2">
                                {t('evaluation.certificate.pageTitle')}
                            </h1>
                            <p className="text-gray-600">
                                {evaluation.title}
                            </p>
                        </div>
                    </div>

                    {/* Certificate Content */}
                    <div className="px-8 py-6">
                        {status === 'ready' && certificate_url ? (
                            <div>
                                <div className="mb-6">
                                    <img
                                        src={certificate_url}
                                        alt={t('evaluation.certificate.imageAlt')}
                                        className="w-full h-auto rounded-lg shadow-md"
                                    />
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <PrimaryButton onClick={handleDownload} size="md">
                                        {t('evaluation.certificate.downloadButton')}
                                    </PrimaryButton>
                                    <button
                                        onClick={handleReturnToCourse}
                                        className="px-6 py-2 rounded-md font-medium transition-colors duration-200 text-gray-700 bg-gray-100 hover:bg-gray-200"
                                    >
                                        {t('evaluation.certificate.returnToCourse')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 flex items-center justify-center">
                                    <svg className="w-8 h-8 text-yellow-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </div>
                                <h2 className="text-xl font-semibold text-gray-800 mb-2">
                                    {t('evaluation.certificate.generatingTitle')}
                                </h2>
                                <p className="text-gray-600 mb-6">
                                    {t('evaluation.certificate.generatingMessage')}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <PrimaryButton onClick={handleRefresh} size="md">
                                        {t('evaluation.certificate.refreshButton')}
                                    </PrimaryButton>
                                    <button
                                        onClick={handleReturnToCourse}
                                        className="px-6 py-2 rounded-md font-medium transition-colors duration-200 text-gray-700 bg-gray-100 hover:bg-gray-200"
                                    >
                                        {t('evaluation.certificate.returnToCourse')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

const Certificate: React.FC<CertificateProps> = (props) => {
    return (
        <ThemeWrapper>
            <CertificateContent {...props} />
        </ThemeWrapper>
    );
};

export default Certificate;
